import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';

type EventInvite = {
	_id: string;
	event_id: string;
	email: string;
	name?: string;
	phone?: string;
	token: string;
	response?: 'attending' | 'declined' | 'maybe' | null;
	responded_at?: string | null;
	sent_at?: string | null;
	createdAt: string;
	updatedAt: string;
};

const RESPONSE_LABELS: Record<string, { text: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
	attending: { text: 'מגיע/ה', color: 'success' },
	declined: { text: 'לא מגיע/ה', color: 'error' },
	maybe: { text: 'עוד לא יודע/ת', color: 'warning' },
};

const formatDate = (value?: string | null) =>
	value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';

const csvEscape = (value: any) => {
	if (value === null || value === undefined) return '';
	const str = String(value);
	if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
	return str;
};

const downloadCsv = (rows: EventInvite[]) => {
	const header = ['שם', 'אימייל', 'טלפון', 'אירוע', 'תשובה', 'נשלח', 'נענה', 'נוצר'];
	const lines = [header.join(',')];
	for (const r of rows) {
		const responseText = r.response ? RESPONSE_LABELS[r.response]?.text || r.response : '';
		lines.push(
			[
				r.name || '',
				r.email,
				r.phone || '',
				r.event_id,
				responseText,
				formatDate(r.sent_at),
				formatDate(r.responded_at),
				formatDate(r.createdAt),
			]
				.map(csvEscape)
				.join(','),
		);
	}
	const csv = '﻿' + lines.join('\n'); // BOM for Excel Hebrew
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `event-invites-${moment().format('YYYY-MM-DD-HHmm')}.csv`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

const EventInvitesList = () => {
	const [data, setData] = useState<EventInvite[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetch = async () => {
			setLoading(true);
			setError(null);
			try {
				const response = await httpClient(`${constants.API_URL}admin/event-invites`, { method: 'GET' });
				setData((response.json as EventInvite[]) || []);
			} catch (err: any) {
				setError('לא הצלחנו לטעון את הנתונים');
			} finally {
				setLoading(false);
			}
		};
		fetch();
	}, []);

	const stats = useMemo(() => {
		const total = data.length;
		const sent = data.filter(d => d.sent_at).length;
		const responded = data.filter(d => d.response).length;
		const attending = data.filter(d => d.response === 'attending').length;
		const declined = data.filter(d => d.response === 'declined').length;
		const maybe = data.filter(d => d.response === 'maybe').length;
		return { total, sent, responded, attending, declined, maybe };
	}, [data]);

	return (
		<Box p={3} dir="rtl">
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="h4">הזמנות לאירועים</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<FaFileCsv />}
					onClick={() => downloadCsv(data)}
					disabled={!data.length}
				>
					ייצוא CSV
				</Button>
			</Stack>

			<Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
				<Chip label={`סה"כ: ${stats.total}`} />
				<Chip label={`נשלחו: ${stats.sent}`} color="info" />
				<Chip label={`ענו: ${stats.responded}`} color="default" />
				<Chip label={`מגיעים: ${stats.attending}`} color="success" />
				<Chip label={`לא מגיעים: ${stats.declined}`} color="error" />
				<Chip label={`לא יודעים: ${stats.maybe}`} color="warning" />
			</Stack>

			{loading && (
				<Stack alignItems="center" p={4}>
					<CircularProgress />
				</Stack>
			)}
			{error && (
				<Typography color="error" mb={2}>
					{error}
				</Typography>
			)}

			{!loading && !error && (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>שם</TableCell>
								<TableCell>אימייל</TableCell>
								<TableCell>טלפון</TableCell>
								<TableCell>אירוע</TableCell>
								<TableCell>תשובה</TableCell>
								<TableCell>נשלח</TableCell>
								<TableCell>נענה</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.map(row => {
								const respMeta = row.response ? RESPONSE_LABELS[row.response] : null;
								return (
									<TableRow key={row._id}>
										<TableCell>{row.name || '—'}</TableCell>
										<TableCell>{row.email}</TableCell>
										<TableCell>{row.phone || '—'}</TableCell>
										<TableCell>{row.event_id}</TableCell>
										<TableCell>
											{respMeta ? (
												<Chip size="small" label={respMeta.text} color={respMeta.color} />
											) : (
												<Chip size="small" label="לא ענה/תה" variant="outlined" />
											)}
										</TableCell>
										<TableCell>{formatDate(row.sent_at)}</TableCell>
										<TableCell>{formatDate(row.responded_at)}</TableCell>
									</TableRow>
								);
							})}
							{!data.length && (
								<TableRow>
									<TableCell colSpan={7} align="center">
										אין הזמנות
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	);
};

export default EventInvitesList;
